import { ExpandMoreIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import PhotoGallery from '@components/FileViewer/PhotoGallery'
import Spinner from '@components/Spinner'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ChatMessage, { type ChatMessageHandlers } from './ChatMessage'
import {
	DaySeparator,
	ScrollToBottomButton,
	StreamFrame,
	StreamNotice,
	StreamNoticeMessage,
	StreamNoticeTitle,
	StreamScroller,
	UnreadDivider,
} from './ChatStream.styles'
import type { ChatPhoto, ChatStreamItem } from './core'
import type { ChatUpdate } from './useChat'

/** How close to the newest Message still counts as at the bottom, so new ones keep it in view. */
const PINNED_SLACK = 40
/** How far from the newest Message counts as scrolled up, which brings the button. */
const SCROLLED_UP_DISTANCE = 300
/** How near the top a scroll up asks for history. */
const LOAD_OLDER_THRESHOLD = 400
const FLASH_MS = 600

type StreamHandlers = Omit<ChatMessageHandlers, 'onOpenPhotos' | 'onOpenMessage'>

interface ChatStreamProps extends StreamHandlers {
	items: ChatStreamItem[]
	lastUpdate: { update: ChatUpdate; sequence: number }
	hasLoaded: boolean
	emptyState: { title: string; message: string } | null
	/** The user scrolled up near the oldest loaded Message. */
	onNearTop: () => void
	/** The ids of the sent Messages on screen; the newest marks the Chat read. */
	onMessagesShown: (messageIds: number[]) => void
}

/**
 * The Chat's stream on screen: its rows, and its scrolling — opening on the "N new"
 * divider or the newest Message, keeping the newest in view as Messages arrive, holding
 * what's on screen in place as history loads on top, and centring a Message the Chat is
 * asked to show. Reports which Messages are on screen while the tab is visible. Ported
 * from iOS `ChatStreamController`.
 */
const ChatStream: React.FC<ChatStreamProps> = ({
	items,
	lastUpdate,
	hasLoaded,
	emptyState,
	onNearTop,
	onMessagesShown,
	...handlers
}) => {
	const scrollerRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)
	/** Whether the newest Message is in view, so new ones should keep it there. */
	const pinned = useRef(true)
	/** The scroll height below the top of the view, as the last scroll left it: what an older page keeps. */
	const fromBottom = useRef(0)
	const lastScrollTop = useRef(0)
	const handledSequence = useRef(0)
	const hasOpened = useRef(false)
	const visibleIds = useRef(new Set<number>())
	const [scrolledUp, setScrolledUp] = useState(false)
	const [flashId, setFlashId] = useState<number | null>(null)
	const [gallery, setGallery] = useState<{ photos: ChatPhoto[]; index: number } | null>(null)
	const showsNotice = items.length === 0

	const reportShown = useCallback(() => {
		if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
		onMessagesShown([...visibleIds.current])
	}, [onMessagesShown])

	const distanceFromBottom = (el: HTMLDivElement) => el.scrollHeight - el.scrollTop - el.clientHeight

	const scrollToBottom = useCallback((smooth: boolean) => {
		const el = scrollerRef.current
		if (!el) return
		pinned.current = true
		el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
	}, [])

	useLayoutEffect(() => {
		const el = scrollerRef.current
		if (!el) return
		/** Opening the Chat lands on the "N new" divider when there is one, else the newest Message. */
		const scrollToFirstUnreadOrBottom = () => {
			const divider = el.querySelector<HTMLElement>('[data-unread-divider]')
			if (divider) el.scrollTop = divider.offsetTop
			else scrollToBottom(false)
		}
		if (lastUpdate.sequence !== handledSequence.current) {
			handledSequence.current = lastUpdate.sequence
			const { update } = lastUpdate
			switch (update.type) {
				case 'opened':
					scrollToFirstUnreadOrBottom()
					hasOpened.current = true
					break
				case 'revealed': {
					const row = el.querySelector<HTMLElement>(`[data-message-id="${update.messageId}"]`)
					if (row) {
						el.scrollTop = row.offsetTop - el.clientHeight / 2 + row.offsetHeight / 2
						setFlashId(update.messageId)
					} else {
						// Deleted beyond the pages loaded: the Chat opens as usual.
						scrollToFirstUnreadOrBottom()
					}
					hasOpened.current = true
					break
				}
				case 'olderLoaded':
					el.scrollTop = el.scrollHeight - fromBottom.current
					break
				case 'sent':
					scrollToBottom(true)
					break
				case 'changed':
					if (pinned.current && hasOpened.current) scrollToBottom(false)
					break
			}
			pinned.current = distanceFromBottom(el) <= PINNED_SLACK
		} else if (pinned.current && hasOpened.current) {
			// A confirmed send, a reaction, a booking's people: the newest stays in view.
			scrollToBottom(false)
		}
	}, [items, lastUpdate, scrollToBottom])

	// Photos and previews load after their rows: the newest Message stays in view as they do.
	useEffect(() => {
		const content = contentRef.current
		if (!content || typeof ResizeObserver === 'undefined') return
		const observer = new ResizeObserver(() => {
			if (pinned.current && hasOpened.current) scrollToBottom(false)
		})
		observer.observe(content)
		return () => observer.disconnect()
	}, [scrollToBottom, showsNotice])

	// Messages count as read once they're on screen, while the tab is.
	useEffect(() => {
		const el = scrollerRef.current
		if (!el || typeof IntersectionObserver === 'undefined') return
		const observer = new IntersectionObserver(
			entries => {
				for (const entry of entries) {
					const id = Number((entry.target as HTMLElement).dataset.messageId)
					if (entry.isIntersecting) visibleIds.current.add(id)
					else visibleIds.current.delete(id)
				}
				if (hasOpened.current) reportShown()
			},
			{ root: el },
		)
		visibleIds.current.clear()
		el.querySelectorAll('[data-message-id]').forEach(row => observer.observe(row))
		return () => observer.disconnect()
	}, [items, reportShown])

	useEffect(() => {
		const handleVisibility = () => reportShown()
		document.addEventListener('visibilitychange', handleVisibility)
		return () => document.removeEventListener('visibilitychange', handleVisibility)
	}, [reportShown])

	useEffect(() => {
		if (flashId === null) return
		const timer = setTimeout(() => setFlashId(null), FLASH_MS)
		return () => clearTimeout(timer)
	}, [flashId])

	const handleScroll = () => {
		const el = scrollerRef.current
		if (!el) return
		const distance = distanceFromBottom(el)
		pinned.current = distance <= PINNED_SLACK
		fromBottom.current = el.scrollHeight - el.scrollTop
		setScrolledUp(distance > SCROLLED_UP_DISTANCE)
		// Only scrolling up asks for history, not the first scroll to the bottom.
		const scrollingUp = el.scrollTop < lastScrollTop.current
		lastScrollTop.current = el.scrollTop
		if (hasOpened.current && scrollingUp && el.scrollTop < LOAD_OLDER_THRESHOLD) onNearTop()
	}

	return (
		<StreamFrame>
			<StreamScroller ref={scrollerRef} onScroll={handleScroll} role="log" aria-label="Messages">
				{showsNotice ? (
					<StreamNotice>
						{emptyState ? (
							<>
								<StreamNoticeTitle>{emptyState.title}</StreamNoticeTitle>
								<StreamNoticeMessage>{emptyState.message}</StreamNoticeMessage>
							</>
						) : (
							!hasLoaded && <Spinner size={32} />
						)}
					</StreamNotice>
				) : (
					<div ref={contentRef}>
						{items.map(item => {
							if (item.type === 'day') return <DaySeparator key={item.id}>{item.title}</DaySeparator>
							if (item.type === 'unreadDivider')
								return (
									<UnreadDivider key={item.id} data-unread-divider>
										{item.title}
									</UnreadDivider>
								)
							return (
								<ChatMessage
									key={item.id}
									row={item.row}
									flash={item.row.delivery.status === 'sent' && item.row.delivery.messageId === flashId}
									onOpenPhotos={(photos, index) => setGallery({ photos, index })}
									{...handlers}
								/>
							)
						})}
					</div>
				)}
			</StreamScroller>
			<ScrollToBottomButton
				type="button"
				$shown={scrolledUp}
				onClick={() => scrollToBottom(true)}
				aria-label="Scroll to latest"
				tabIndex={scrolledUp ? 0 : -1}
			>
				<ExpandMoreIcon sx={{ fontSize: iconSizes.lg }} />
			</ScrollToBottomButton>
			<PhotoGallery photos={gallery?.photos ?? []} startIndex={gallery?.index ?? null} onClose={() => setGallery(null)} />
		</StreamFrame>
	)
}

export default ChatStream
